import type { FC } from 'react'
import { useCallback, useState } from 'react'
import { WizardModal } from '@/common/components'
import { useApiKeyGeneration } from '../hooks'
import { WelcomeStep } from './steps/WelcomeStep'
import { ConfigurationStep } from './steps/ConfigurationStep'
import { ApiKeyDisplayStep } from './steps/ApiKeyDisplayStep'
import { DEFAULT_ENVIRONMENT } from '../constants'
import type { ApiKeyFormState, WizardStep } from '../types'
import { expirationToIsoTimestamp } from '../types'

interface ApiKeyGenerationModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

const INITIAL_FORM: ApiKeyFormState = {
  name: '',
  environment: DEFAULT_ENVIRONMENT,
  expirationDays: 30,
  customExpirationDate: null,
}

export const ApiKeyGenerationModal: FC<ApiKeyGenerationModalProps> = ({ open, onClose }) => {
  const [step, setStep] = useState<WizardStep>('welcome')
  const [form, setForm] = useState<ApiKeyFormState>(INITIAL_FORM)

  const { generateAsync, isGenerating, error, generatedKey, reset } = useApiKeyGeneration()

  const handleClose = useCallback(() => {
    setStep('welcome')
    setForm(INITIAL_FORM)
    reset()
    onClose()
  }, [onClose, reset])

  const handleWelcomeNext = useCallback(() => {
    setStep('configuration')
  }, [])

  const handleConfigurationBack = useCallback(() => {
    setStep('welcome')
  }, [])

  const handleGenerate = useCallback(
    async (values: ApiKeyFormState) => {
      setForm(values)
      const expiresAt = expirationToIsoTimestamp(values.expirationDays, values.customExpirationDate)
      const request = {
        name: values.name,
        environment: values.environment,
        ...(expiresAt && { expiresAt }),
      }
      const result = await generateAsync(request)
      if (result.success) {
        setStep('display')
      }
    },
    [generateAsync],
  )

  const hideCloseButton = step === 'display'

  return (
    <WizardModal open={open} onClose={handleClose} hideCloseButton={hideCloseButton}>
      {step === 'welcome' && <WelcomeStep onNext={handleWelcomeNext} onClose={handleClose} />}

      {step === 'configuration' && (
        <ConfigurationStep
          initialValues={form}
          isGenerating={isGenerating}
          error={error}
          onGenerate={handleGenerate}
          onBack={handleConfigurationBack}
        />
      )}

      {step === 'display' && generatedKey && (
        <ApiKeyDisplayStep apiKey={generatedKey} onClose={handleClose} />
      )}
    </WizardModal>
  )
}
